import * as React from 'react'
import { Text, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Svg, { Line, Path, Circle, G } from 'react-native-svg'

interface Item {
  value: number
  index: number
}

interface LineItem {
  items: Item[]
  color: string
}

interface Props {
  data: LineItem[]
  labels: string[] // x axis
  size?: 'small' | 'large'
}

/**
 * Specific chart to Viper, draw line based on given percents
 * @returns Line Chart
 */
const LineChart = ({ data, labels, size = 'large' }: Props) => {
  const X_AXIS_SIZE = React.useMemo(() => (size === 'large' ? 50 : 30), [size])
  const Y_AXIS_SIZE = React.useMemo(() => (size === 'large' ? 100 : 50), [size])

  const MIN = React.useMemo(
    () => Math.max(Math.min(...data.flatMap((d) => d.items.map((it) => it.value))) - 10, 0),
    [data]
  )
  const MAX = React.useMemo(
    () => Math.min(Math.max(...data.flatMap((d) => d.items.map((it) => it.value))) + 10, 100),
    [data]
  )

  const RANGE = React.useMemo(() => Math.ceil((MAX - MIN) / 10) + 1, [MAX, MIN])
  const MAX_AXIS = React.useMemo(() => Math.ceil(MAX / 10) * 10, [MAX])
  const MIN_AXIS = React.useMemo(() => MAX_AXIS - (RANGE - 1) * 10, [MAX_AXIS, RANGE])

  const [dimensions, setDimensions] = React.useState<{ height: number; width: number }>()

  const ITEM_HEIGHT = React.useMemo(
    () => (dimensions ? (dimensions.height - X_AXIS_SIZE) / RANGE : 0),
    [dimensions, X_AXIS_SIZE, RANGE]
  )

  const ITEM_WIDTH = React.useMemo(
    () => (dimensions ? (dimensions.width - Y_AXIS_SIZE) / labels.length : 0),
    [dimensions, Y_AXIS_SIZE, labels]
  )

  // no data
  if (data.flatMap((d) => d.items).length === 0) {
    return <Empty key="no_data" />
  }

  if (!dimensions) {
    return <Empty key="no_layout" onLayout={(evt) => setDimensions((prev) => prev || evt.nativeEvent.layout)} />
  }

  const renderYAxis = (idx: number) => {
    const originX = 0
    const originY = idx * ITEM_HEIGHT

    const value = `${MAX_AXIS - idx * 10} %`

    return (
      <ForeignObject key={`y_${value}`} x={originX} y={originY} width={Y_AXIS_SIZE} height={ITEM_HEIGHT}>
        <AxisContainer width={Y_AXIS_SIZE} height={ITEM_HEIGHT}>
          <AxisText size={size}>{value}</AxisText>
        </AxisContainer>
      </ForeignObject>
    )
  }

  const renderYScale = (idx: number) => {
    const originY = idx * ITEM_HEIGHT

    return (
      <Line
        key={`y_${idx}`}
        x1={Y_AXIS_SIZE}
        x2={dimensions.width}
        y1={originY + ITEM_HEIGHT / 2}
        y2={originY + ITEM_HEIGHT / 2}
        stroke={theme.colors.border}
        strokeWidth={1}
      />
    )
  }

  const renderXAxis = (label: string, idx: number) => {
    const originX = idx * ITEM_WIDTH + Y_AXIS_SIZE
    const originY = dimensions.height - X_AXIS_SIZE

    return (
      <ForeignObject key={label} x={originX} y={originY} width={ITEM_WIDTH} height={X_AXIS_SIZE}>
        <AxisContainer width={ITEM_WIDTH} height={X_AXIS_SIZE}>
          <AxisText size={size}>{label}</AxisText>
        </AxisContainer>
      </ForeignObject>
    )
  }

  const renderItems = (group: LineItem, idx: number) => {
    const originX = Y_AXIS_SIZE
    const originY = 0

    const surfaceY = dimensions.height - X_AXIS_SIZE
    const surfaceTOP = MAX_AXIS + 5
    const surfaceBOTTOM = MIN_AXIS - 5

    const positions = group.items.map((item, index) => {
      const x = originX + item.index * ITEM_WIDTH + ITEM_WIDTH / 2

      const offsetY = (surfaceTOP - item.value) / (surfaceTOP - surfaceBOTTOM)
      const y = item.value === MAX ? ITEM_HEIGHT / 2 : originY + offsetY * surfaceY

      return { x, y }
    })

    const line = positions.map((item, index) => `${index === 0 ? 'M' : 'L'}${item.x} ${item.y}`).join(' ')

    return (
      <G key={`line_${idx}`}>
        <Path d={line} stroke={group.color} strokeWidth={1} fill="none" />
        {positions.map((p) => (
          <Circle key={p.x} cx={p.x} cy={p.y} r={8} fill={group.color} />
        ))}
      </G>
    )
  }

  return (
    <View style={{ width: dimensions.width, height: dimensions.height }}>
      <Svg height={dimensions.height} width={dimensions.width}>
        {/* YScale */}
        {new Array(RANGE).fill(0).map((z, idx) => renderYScale(idx))}

        {/* Content */}
        {data.map(renderItems)}
      </Svg>

      {/* YAxis */}
      {new Array(RANGE).fill(0).map((z, idx) => renderYAxis(idx))}
      {/* XAxis */}
      {labels.map(renderXAxis)}
    </View>
  )
}

export default LineChart

const Empty = styled(View)`
  flex: 1;
`

const AxisContainer = styled(View)<{ height: number; width: number }>`
  align-items: center;
  justify-content: center;
  height: ${(props) => props.height}px;
  width: ${(props) => props.width}px;
`

const AxisText = styled(Text)<{ size: 'small' | 'large' }>`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  font-size: ${(props) => (props.size === 'large' ? 16 : 12)};
  line-height: ${(props) => (props.size === 'large' ? 16 : 12)};
  text-align: center;
`

const ForeignObject = styled(View)<{ x: number; y: number; width: number; height: number }>`
  position: absolute;
  top: ${(props) => props.y}px;
  left: ${(props) => props.x}px;
  height: ${(props) => props.height}px;
  width: ${(props) => props.width}px;
`
